import type { ExternalAccountID } from "@repo/db/branded-types";
import { EXTERNAL_ACCOUNT_ID_COOKIE_NAME } from "@repo/sdk";
import { AUTH_COOKIE_NAME, type AuthCookie } from "./auth-cookie-constants";

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(name: string, value: string): void {
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${60 * 60 * 24 * 365}; SameSite=Lax${secure}`;
}

/**
 * Reads the auth cookie from the client side.
 */
export function getAuthCookie(): AuthCookie | null {
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${AUTH_COOKIE_NAME}=([^;]*)`)
  );
  if (!match) return null;
  try {
    return JSON.parse(decodeURIComponent(match[1]));
  } catch {
    return null;
  }
}

/**
 * Sets the auth cookie from the client side.
 * Stores externalAccountId and optional customerId as a JSON cookie.
 */
export function setAuthCookie(data: AuthCookie): void {
  const value = encodeURIComponent(JSON.stringify(data));
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${AUTH_COOKIE_NAME}=${value}; path=/; max-age=${60 * 60 * 24 * 365}; SameSite=Lax${secure}`;
}

/**
 * Ensures both auth cookies reflect the SDK-provided externalAccountId.
 *
 * The SDK is the source of truth for identity. If either the auth cookie or
 * the dedicated EXTERNAL_ACCOUNT_ID_COOKIE_NAME cookie disagrees, both are
 * overwritten — otherwise a stale cookie (e.g. persisted in WKWebView across
 * test runs) shadows a fresh SDK context and reads route to the wrong account.
 */
export function ensureAuthCookie(externalAccountId: ExternalAccountID): void {
  const existing = getAuthCookie();
  const externalIdCookie = readCookie(EXTERNAL_ACCOUNT_ID_COOKIE_NAME);

  if (
    existing?.externalAccountId === externalAccountId &&
    externalIdCookie === externalAccountId
  ) {
    return;
  }

  // Only carry forward fields like customerId when the account is unchanged —
  // otherwise they belong to a previous user and would leak across identities.
  const carryForward =
    existing?.externalAccountId === externalAccountId ? existing : {};
  setAuthCookie({ ...carryForward, externalAccountId });
  writeCookie(EXTERNAL_ACCOUNT_ID_COOKIE_NAME, externalAccountId);
}
